import { Tuple } from '@skylab-shared/types';

import { Singleton } from '@skylab-shared/utils';
import { ConsoleLogger } from '../../logger';
import { JoinPoint, JoinPointConstructor, JoinPointContext } from './join-point';
import { JoinPointAsync, JoinPointAsyncConstructor, JoinPointAsyncContext } from './join-point-async';


export type Advice<TJoinPointContext extends JoinPointContext = JoinPointContext> = (context: TJoinPointContext) => void;

export type AdviceAsync<TJoinPointContext extends JoinPointAsyncContext = JoinPointAsyncContext> = (context: TJoinPointContext) => Promise<void>;

@Singleton
export class Weaver {
    private readonly logger: ConsoleLogger = new ConsoleLogger(Weaver.name);
    private readonly registrations: Tuple<JoinPointConstructor | JoinPointAsyncConstructor, (Advice | AdviceAsync)[]>[] = [];

    public register<TJoinPointContext extends JoinPointContext>(joinPointType: JoinPointConstructor<TJoinPointContext>, advice: Advice<TJoinPointContext>): Function;
    public register<TJoinPointContext extends JoinPointAsyncContext>(joinPointType: JoinPointAsyncConstructor<TJoinPointContext>, advice: AdviceAsync<TJoinPointContext>): Function;
    public register(joinPointType: any, advice: any): Function {
        let registration = this.registrations
            .filter(([type]) => type === joinPointType)
            .shift();
        if (!registration) {
            registration = [joinPointType, []];
            this.registrations.push(registration);
        }
        registration[1].push(advice);
        return () => registration[1].splice(registration[1].indexOf(advice), 1);
    }

    public weave(joinPoint: JoinPoint<JoinPointContext>): void {
        const [_, advices] = <[any, Advice[]]>(this.registrations
            .filter(([joinPointType]) => joinPoint instanceof joinPointType)
            .shift() || []);
        advices && advices.forEach(advice => advice(joinPoint.context));
    }

    public async weaveAsync(joinPoint: JoinPointAsync<JoinPointAsyncContext>): Promise<void> {
        const [_, advices] = <[any, AdviceAsync[]]>(this.registrations
            .filter(([joinPointType]) => joinPoint instanceof joinPointType)
            .shift() || []);
        return advices
            .reduce((prev, curr) => prev.then(() => curr(joinPoint.context)), Promise.resolve())
            .catch(reason => this.logger.error(reason));
    }
}

export const WeaverBase = new Weaver();
